import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConversationList from '@/components/messages/ConversationList';
import ChatWindow from '@/components/messages/ChatWindow';
import UserSearch from '@/components/messages/UserSearch';
import { Edit, MessageCircle, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage({ user }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutsRef = useRef({});

  useEffect(() => { fetchConversations(); }, []);

  // WebSocket with typing tracking
  useEffect(() => {
    const token = localStorage.getItem('dharmbook_token');
    if (!token) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const socket = new WebSocket(`${wsUrl}/ws/${token}`);
    socket.onopen = () => {
      socket._pi = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send('ping');
      }, 30000);
    };
    socket.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message' || data.type === 'messages_seen' || data.type === 'message_reaction') {
          fetchConversations();
        }
        // Track typing per conversation
        if (data.type === 'typing' && data.data?.conversation_id) {
          const convId = data.data.conversation_id;
          setTypingUsers(prev => ({ ...prev, [convId]: true }));
          clearTimeout(typingTimeoutsRef.current[convId]);
          typingTimeoutsRef.current[convId] = setTimeout(() => {
            setTypingUsers(prev => {
              const next = { ...prev };
              delete next[convId];
              return next;
            });
          }, 2500);
        }
      } catch {}
    };
    socket.onclose = () => { if (socket._pi) clearInterval(socket._pi); };
    setWs(socket);
    return () => {
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      if (socket._pi) clearInterval(socket._pi);
      socket.close();
    };
  }, []);

  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) setActiveConversation(conv);
    }
  }, [conversationId, conversations]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch {} finally { setLoading(false); }
  };

  const handleSelectConversation = (conv) => {
    setActiveConversation(conv);
    setShowSearch(false);
    navigate(`/messages/${conv.id}`);
  };

  const handleStartConversation = async (userId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/conversations/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newConv = response.data;
      setConversations(prev => {
        const exists = prev.find(c => c.id === newConv.id);
        if (exists) return prev;
        return [newConv, ...prev];
      });
      setActiveConversation(newConv);
      setShowSearch(false);
      navigate(`/messages/${newConv.id}`);
    } catch {}
  };

  const handleBack = () => { setActiveConversation(null); navigate('/messages'); };
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const filtered = searchQuery
    ? conversations.filter(c =>
        c.other_user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  return (
    <div className={`${conversationId ? 'h-screen' : 'h-[calc(100vh-3.5rem-3.5rem)] md:h-[calc(100vh-3.5rem)]'} flex bg-white overflow-hidden ${conversationId ? 'mx-0 my-0 rounded-none border-0' : 'mx-0 sm:mx-2 md:mx-4 my-0 sm:my-2 md:my-4 rounded-none sm:rounded-2xl border-0 sm:border border-slate-200'} w-full max-w-[100vw] shadow-sm`} data-testid="messages-page">
      {/* Sidebar */}
      <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-[340px] lg:w-[380px] border-r border-slate-100 max-w-full bg-[#FEFCF9]`}>
        {/* Header */}
        <div className="px-4 pt-4 pb-3 bg-white border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Yatra One', cursive" }} data-testid="messages-title">Messages</h1>
              {totalUnread > 0 && (
                <span className="bg-[#FF9933] text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-in fade-in" data-testid="total-unread-badge">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </div>
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="w-9 h-9 rounded-full bg-orange-50 hover:bg-orange-100 flex items-center justify-center transition-all active:scale-90"
              data-testid="new-message-btn"
            >
              <Edit className="w-4 h-4 text-[#FF9933]" />
            </button>
          </div>
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or message..."
              className="w-full pl-9 pr-8 py-2.5 bg-slate-100/80 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:bg-white transition-all placeholder:text-slate-400"
              data-testid="search-conversations"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-200" data-testid="clear-search-btn">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>
        </div>

        {showSearch ? (
          <UserSearch onSelectUser={handleStartConversation} onClose={() => setShowSearch(false)} />
        ) : (
          <ConversationList
            conversations={filtered}
            activeId={activeConversation?.id}
            onSelect={handleSelectConversation}
            loading={loading}
            currentUserId={user?.id}
            typingUsers={typingUsers}
          />
        )}
      </div>

      {/* Chat Area */}
      <div className={`${activeConversation ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {activeConversation ? (
          <ChatWindow
            conversation={activeConversation}
            currentUser={user}
            ws={ws}
            onConversationUpdate={fetchConversations}
            onBack={handleBack}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFBF5 50%, #FFF7ED 100%)' }}>
            <div className="text-center px-8">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-9 h-9 text-[#FF9933]" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">Your Messages</h3>
              <p className="text-sm text-slate-400 max-w-[280px]">
                Send peaceful messages to your DharmBook community
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
